import { gql } from '@apollo/client';
import { isNumber, omit } from 'lodash';
import {
  IUser,
  UpdateUserAvatarFields,
  UserSettings,
} from 'spw-redux/modules/user/types';
import {
  packPolygonsMatrixForPersistence,
  removeTypeNameFromObject,
} from 'spw-shared/utils';
import { IDeviceInfo, IFeedback } from 'spw-redux/modules/feedback/types';
import {
  IWorksheet,
  ICategoryNote,
  IWorksheetRow,
  IChildWorksheetSpaceType,
} from 'spw-redux/modules/calculators/types';
import { INote } from 'spw-redux/modules/notes/types';
import {
  BillingPeriod,
  Organization,
  UpdateOrganizationInput,
} from 'spw-redux/modules/organization/types';

import { SortingDirection } from 'spw-components/collab/types';
import { ISpaceType } from 'spw-redux/modules/space_types/types';
import { PROJECT_FIELDS } from 'spw-api/project_fragments';
import {
  ISpace,
  IFloorplan,
  ICalibration,
  ISpaceShape,
  ICluster,
} from './redux/modules/floorplan/types';

import {
  IProject,
  IBuilding,
  IFloor,
  IDepartment,
  IProjectColors,
  ProjectStatus,
} from './redux/modules/projects/types';
import {
  ISort,
  IUserFilter,
  IProjectFilter,
  IActivityLogFilter,
} from './admin/data-provider';
import { graphqlClient } from './api/utils';

export * as Project from './api/projects';
export * as Headcount from './api/headcount';
export * as SpaceType from './api/space-type';
export * as Worksheet from './api/worksheet';
export * as FloorPlan from './api/floorplan';
export * as Space from 'spw-api/space';
export * as Scenario from './api/scenario';
export * as TotalProgram from './api/total_program';

export const getOrganizations = () =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOrganizations {
        organizations {
          id
          name
        }
      }
    `,
  });

export const listOrganizations = (
  pageNumber: number,
  pageSize: number,
  sort: ISort,
  filter: IUserFilter
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query ListOrganizations($filter: OrgFilter, $sort: OrganizationSortOptions!) {
          organizationList(
              sort: $sort
              pageNumber: ${pageNumber}
              pageSize: ${pageSize}
              filter: $filter
          ) {
              total
              organizations {
                  id
                  name
                  numberOfUsers
                  numberOfDeactivatedUsers
                  numberOfProjects
                  projects {
                      archived
                  }
                  isActive
                  hasAccessToBenchmarking
                  hasAccessToUnitConversion
              }
          }
      }
  `,
    variables: { filter, sort: { [sort.field]: sort.order } },
  });

export const getActivityLogs = (
  page: number,
  perPage: number,
  sort: ISort,
  filter: IActivityLogFilter
) => {
  return graphqlClient.query<{
    activityLogs: { total: number; activityLogs: Record<string, unknown>[] };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
        query GetActivityLogs($userId: Int) {
            activityLogs(
                sort: {${sort.field}: ${sort.order}}
                pageNumber: ${page}
                pageSize: ${perPage}
                filter: {
                    userId: $userId
                ${
                  filter?.organizationId
                    ? `organizationId: ${filter.organizationId}`
                    : ''
                }}
            ) {
                total
                activityLogs {
                    id
                    deviceName
                    deviceId
                    fingerprint
                    isMobile
                    platform
                    platformName
                    timeZone
                    loginAt
                    ipAddress
                    browserOrVersion
                    user {
                        id
                        name
                        email
                    }
                }
            }
        }
    `,
    variables: {
      userId: filter.userId,
    },
  });
};

export const listOrgAdmins = (organizationId: number) => {
  return graphqlClient.query<{
    orgAdmins: IUser[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOrgAdmins($organizationId: Int!) {
        orgAdmins: listOrgAdmins(organizationId: $organizationId) {
          id
          email
          name
          isSuperAdmin
          isOrgAdmin
          isActive
          organizationId
          hasAvatar
          thumbnailS3Path
        }
      }
    `,
    variables: {
      organizationId,
    },
  });
};

export const listOrgProjects = (organizationId: number) => {
  return graphqlClient.query<{
    orgProjects: Partial<IProject>[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOrgProjects($organizationId: Int!) {
        orgProjects: orgProjects(organizationId: $organizationId) {
          id
          projectName
          type
          projectNumber
          status
        }
      }
    `,
    variables: {
      organizationId,
    },
  });
};

export const getArchivedProjects = (
  page: number,
  perPage: number,
  sort: ISort,
  filter: IProjectFilter
) => {
  return graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
        query GetArchivedProjects {
            archivedProjects(
                sort: {${sort.field}: ${sort.order}}
                pageNumber: ${page}
                pageSize: ${perPage}
                filter: {${
                  filter?.organizationId
                    ? `organizationId: ${filter.organizationId}`
                    : ''
                }}
            ) {
                total
                projects {
                    id
                    projectNumber
                    projectName
                    archived
                    archivedAt
                    archivedUser{
                        name
                        email
                    }
                    createdByUser{
                        name
                        email
                    }
                }
            }
        }
    `,
  });
};

export const listOrganizationsForExport = () => {
  return graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query ListOrganizationsForExport {
        exportOrganizations {
          id
          name
          isActive
          photoCount
          users {
            id
            email
            name
            isSuperAdmin
            isActive
            organizationId
          }
          projects {
            id
            insertedAt
            city
            state
            projectName
            projectNumber
            createdByUser {
              id
              email
              name
            }
            type
            buildings {
              id
              city
              state
              countryCode
              streetAddress
              programmingUsf
              floors {
                id
                name
                totalFloorUsf
                isMeasuredFromFloorplan
              }
            }
          }
        }
      }
    `,
  });
};

export const createOrganization = (organization: Organization) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateOrganization($organization: OrganizationInput!) {
        organization: createOrganization(organization: $organization) {
          id
          name
          notes
          isActive
        }
      }
    `,
    variables: {
      organization: {
        name: organization.name,
        notes: organization.notes || '',
        hasAccessToBenchmarking: organization.hasAccessToBenchmarking,
        hasAccessToUnitConversion: organization.hasAccessToUnitConversion,
        hasAccessToAiTools: organization.hasAccessToAITools,
      },
    },
  });

export const cloneOrgDataIntoOrg = (
  sourceOrgId: number,
  destinationOrgId: number,
  projectOwnerId?: number,
  selectedProjectIds?: number[]
) =>
  graphqlClient.mutate<{
    cloningResults: { clonedClientIt: number; clonedProjectsCount: number }[];
  }>({
    mutation: gql`
      mutation cloneOrgDataIntoOrg(
        $destinationOrgId: Int!
        $sourceOrgId: Int!
        $projectOwnerId: Int
        $selectedProjectIds: [Int]
      ) {
        cloningResults: cloneOrgDataIntoOrg(
          destinationOrgId: $destinationOrgId
          sourceOrgId: $sourceOrgId
          projectOwnerId: $projectOwnerId
          selectedProjectIds: $selectedProjectIds
        ) {
          clonedClientId
          clonedProjectsCount
        }
      }
    `,
    variables: {
      sourceOrgId,
      destinationOrgId,
      projectOwnerId,
      selectedProjectIds,
    },
  });

export const updateOrganization = (organization: UpdateOrganizationInput) =>
  graphqlClient.query<{ organization: Organization }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation UpdateOrganization($organization: OrganizationInput!) {
        organization: updateOrganization(organization: $organization) {
          id
          name
          notes
          isActive
          hasLogo
          thumbnailS3Path
          organizationColors {
            id
            meetingSpaces
            amenitySpaces
            supportSpaces
            sharedSpaces
            otherSpaces
            individualWorkspaces
            customWorkspaceColors
            customMeetingSpaceColors
            customAmenitySpaceColors
            customSupportSpaceColors
          }
        }
      }
    `,
    variables: { organization },
  });

export const getOrganization = (id: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOrganization {
          organization(id: ${id}) {
              id
              name
              notes
              isActive
              hasLogo
              thumbnailS3Path
              hasAccessToBenchmarking
              hasAccessToUnitConversion
              hasAccessToAITools
              organizationColors {
                  id
                  meetingSpaces
                  amenitySpaces
                  supportSpaces
                  sharedSpaces
                  otherSpaces
                  individualWorkspaces
                  customWorkspaceColors
                  customMeetingSpaceColors
                  customAmenitySpaceColors
                  customSupportSpaceColors
              }
          }
      }
  `,
  });

export const fetchOrgActivitySpreadsheet = (
  orgId: number,
  billingDate: string
) => {
  return graphqlClient.query<{ spreadsheet: { name: string; data: string } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchOrgActivitySpreadsheet($orgId: Int!, $billingDate: String) {
        spreadsheet: orgActivitySpreadsheet(
          orgId: $orgId
          billingDate: $billingDate
        ) {
          name
          data
        }
      }
    `,
    variables: { orgId, billingDate },
  });
};

export const fetchBillingPeriods = (orgId: number) => {
  return graphqlClient.query<{ billingPeriods: Omit<BillingPeriod, 'id'>[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchBillingPeriods($orgId: Int!) {
        billingPeriods(orgId: $orgId) {
          endDate
          startDate
          prettyName
        }
      }
    `,
    variables: {
      orgId,
    },
  });
};

export const listUsersByOrganizationWithSuperAdmins = (
  id: number,
  pageNumber: number,
  pageSize: number,
  sort: ISort
) => {
  return graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
        query ListUsersByOrganizationWithSuperAdmins {
            organizationUsers: listPlatformUsers(
                sort: {${sort.field}: ${sort.order}}
                page: ${pageNumber}
                perPage: ${pageSize}
            ) {
                total
                users {
                    id
                    email
                    name
                    isSuperAdmin
                    isOrgAdmin
                    isActive
                    organizationId
                    hasAvatar
                    thumbnailS3Path
                }
            }
        }
    `,
  });
};

export const fetchUser = () =>
  graphqlClient.query<{ currentUser: IUser }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchUser {
        currentUser {
          name
          id
          email
          isActive
          hasAvatar
          thumbnailS3Path
          organizationId
          isSuperAdmin
          isOrgAdmin
          adminTeams
          settings {
            measurementSystemWeb
          }
        }
      }
    `,
  });

export const fetchVersion = () =>
  graphqlClient.query<{ currentVersion: string }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchVersion {
        currentVersion
      }
    `,
  });

export const getUser = (id: number) =>
  graphqlClient.query<{ user: IUser }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetUser {
          user(id: ${id}) {
              id
              email
              isSuperAdmin
              isOrgAdmin
              name
              hasAvatar
              thumbnailS3Path
              isActive
              allowPasswordAuth
              organizationId
              numberOfProjects
              numberOfTeams
              adminTeams
              officeLocation
              professionalRole
          }
      }
  `,
  });

export const updateUser = (user: Partial<IUser>) => {
  return graphqlClient.mutate({
    mutation: gql`
      mutation UpdateUser(
        $id: Int!
        $name: String
        $email: String
        $isActive: Boolean
        $allowPasswordAuth: Boolean
        $isSuperAdmin: Boolean
        $isOrgAdmin: Boolean
        $officeLocation: String
        $professionalRole: String
        $organizationId: Int!
      ) {
        user: updateUser(
          id: $id
          name: $name
          email: $email
          isActive: $isActive
          allowPasswordAuth: $allowPasswordAuth
          isOrgAdmin: $isOrgAdmin
          isSuperAdmin: $isSuperAdmin
          organizationId: $organizationId
          officeLocation: $officeLocation
          professionalRole: $professionalRole
        ) {
          id
          name
          email
          isSuperAdmin
          isOrgAdmin
          isActive
          allowPasswordAuth
          organizationId
        }
      }
    `,
    variables: user,
  });
};

export const updateUserAvatar = ({
  id,
  image,
  organizationId,
}: UpdateUserAvatarFields) =>
  graphqlClient.mutate<{
    user: Pick<IUser, 'id' | 'hasAvatar' | 'thumbnailS3Path'>;
  }>({
    mutation: gql`
      mutation UpdateUserAvatar {
          user: updateUserAvatar(
              id: ${id}
              image: "${image}"
              organizationId: ${organizationId}
          ) {
              id
              hasAvatar
              thumbnailS3Path
          }
      }
  `,
  });

export const updateUserSettings = (params: UserSettings) =>
  graphqlClient.mutate<IUser>({
    mutation: gql`
      mutation UpdateUserSettings(
        $measurementSystemWeb: String
        $measurementSystemMobile: String
      ) {
        user: updateUserSettings(
          measurementSystemWeb: $measurementSystemWeb
          measurementSystemMobile: $measurementSystemMobile
        ) {
          id
          settings {
            measurementSystemWeb
          }
        }
      }
    `,
    variables: params,
  });

export const deleteUser = (id: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteUser {
          deleteUser(id: ${id}) {
              id
              name
              email
              isActive
          }
      }
  `,
  });

export const createUser = (user: IUser) =>
  graphqlClient.mutate<{
    createUser: IUser;
  }>({
    mutation: gql`
      mutation CreateUser(
        $name: String!
        $email: String!
        $allowPasswordAuth: Boolean
        $organizationId: Int!
        $isOrgAdmin: Boolean
        $isSuperAdmin: Boolean
        $officeLocation: String
        $professionalRole: String
      ) {
        createUser(
          name: $name
          email: $email
          allowPasswordAuth: $allowPasswordAuth
          isOrgAdmin: $isOrgAdmin
          isSuperAdmin: $isSuperAdmin
          organizationId: $organizationId
          officeLocation: $officeLocation
          professionalRole: $professionalRole
        ) {
          id
          name
          email
          isSuperAdmin
          isOrgAdmin
          isActive
          allowPasswordAuth
          organizationId
        }
      }
    `,
    variables: {
      ...user,
      name: user.name || '',
      email: user.email || '',
    },
  });

export const deleteManyUsers = (ids: number[]) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteManyUsers{
          deleteManyUsers(ids: ${ids})
      }
  `,
  });

export const listUsersWithSuperAdmins = (
  page: number,
  perPage: number,
  filter: IUserFilter,
  sort?: { [field: string]: SortingDirection }
) => {
  return graphqlClient.query<{
    listUsersWithSuperAdmins: { total: number; users: IUser[] };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query ListUsersWithSuperAdmins(
        $sort: UserSortOptions
        $page: Int
        $perPage: Int
        $filter: UserFilter
      ) {
        listUsersWithSuperAdmins: listPlatformUsers(
          sort: $sort
          page: $page
          perPage: $perPage
          filter: $filter
        ) {
          total
          users {
            id
            email
            name
            isSuperAdmin
            isOrgAdmin
            isActive
            allowPasswordAuth
            totalFingerprints
            lastLoginAt
            numberOfProjects
            numberOfTeams
            adminTeams
            officeLocation
            hasAvatar
            thumbnailS3Path
            organization {
              id
              name
            }
            activityLog {
              id
              fingerprint
              loginAt
              isMobile
            }
          }
        }
      }
    `,
    variables: { page, perPage, sort, filter },
  });
};

export const getMobileVersionsSettings = () =>
  graphqlClient.query<{
    settings: {
      minimumAndroidAppVersion: string;
      minimumAndroidOsVersion: string;
      minimumIosAppVersion: string;
      minimumIosOsVersion: string;
      recommendedAppVersion: string;
    };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetMobileVersionsSettings {
        settings: getMobileVersionsSettings {
          minimumAndroidAppVersion
          minimumAndroidOsVersion
          minimumIosAppVersion
          minimumIosOsVersion
          recommendedAppVersion
        }
      }
    `,
  });

export const setMobileVersionsSettings = (settings: {
  minimumAndroidAppVersion: string;
  minimumAndroidOsVersion: string;
  minimumIosAppVersion: string;
  minimumIosOsVersion: string;
  recommendedAppVersion: string;
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation SetMobileVersionsSettings(
        $settings: MobileVersionsSettingsInput!
      ) {
        settings: setMobileVersionsSettings(settings: $settings) {
          minimumAndroidAppVersion
          minimumAndroidOsVersion
          minimumIosAppVersion
          minimumIosOsVersion
          recommendedAppVersion
        }
      }
    `,
    variables: { settings },
  });

export const listUsers = (
  page: number,
  perPage: number,
  filter: IUserFilter,
  sort?: { [field: string]: SortingDirection }
) => {
  return graphqlClient.query<{ listUsers: { total: number; users: IUser[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query ListUsers(
        $page: Int
        $perPage: Int
        $sort: UserSortOptions
        $filter: UserFilter
      ) {
        listUsers(
          page: $page
          perPage: $perPage
          sort: $sort
          filter: $filter
        ) {
          total
          users {
            id
            email
            name
            isSuperAdmin
            isOrgAdmin
            isActive
            totalFingerprints
            lastLoginAt
            numberOfEditableProjects
            numberOfOwnedProjects
            numberOfProjects
            numberOfTeams
            adminTeams
            officeLocation
            professionalRole
            hasAvatar
            thumbnailS3Path
            organization {
              id
              name
            }
            activityLog {
              id
              fingerprint
              loginAt
              isMobile
            }
          }
        }
      }
    `,
    variables: { page, perPage, sort, filter },
  });
};

export const getUsersByOrganization = (organizationId: number) => {
  return graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
        query GetUsersByOrganization {
            users: getUsersByOrganization(
                organizationId: ${organizationId}
            ) {
                id,
                email,
                name,
            }
        }
    `,
  });
};

export const getSpaceTypeCategories = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetSpaceTypeCategories {
          spaceTypeCategories (projectId: ${projectId}) {
              id
              category
              name
              spaceTypes {
                  id
                  name
                  spaceType
                  icon
                  children {
                      id
                      name
                      spaceType
                      icon
                  }
              }
          }
      }
  `,
  });

export const getDepartmentsByProjectId = (id: IDepartment['projectId']) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDepartmentsByProjectId {
          departmentsByProjectId(projectId: ${id}) {
              id
              name
              secondaryIdentifier
              headcountClient
              headcountArchitect
              index
          }
      }
  `,
  });

export const createNewDepartment = (department: IDepartment) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateNewDepartment(
        $projectId: Int!
        $name: String!
        $secondaryIdentifier: String
        $headcountArchitect: Int
        $headcountClient: Int
      ) {
        createNewDepartment(
          projectId: $projectId
          name: $name
          secondaryIdentifier: $secondaryIdentifier
          headcountClient: $headcountClient
          headcountArchitect: $headcountArchitect
        ) {
          projectId
          headcountArchitect
          headcountClient
          name
          secondaryIdentifier
        }
      }
    `,
    variables: {
      ...department,
      secondaryIdentifier: department.secondaryIdentifier || '',
    },
  });

export const updateDepartment = (department: IDepartment) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation UpdateDepartment(
        $id: Int!
        $name: String!
        $secondaryIdentifier: String
        $headcountArchitect: Int
        $headcountClient: Int
        $index: Int
      ) {
        updateDepartment(
          id: $id
          name: $name
          secondaryIdentifier: $secondaryIdentifier
          headcountClient: $headcountClient
          headcountArchitect: $headcountArchitect
          index: $index
        ) {
          id
          name
          secondaryIdentifier
          headcountArchitect
          headcountClient
          projectId
          index
        }
      }
    `,
    variables: {
      ...department,
      secondaryIdentifier: department.secondaryIdentifier || '',
    },
  });

export const deleteDepartment = (departmentId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteDepartment {
          deleteDepartment(
              id: ${departmentId}
          ) {
              id
              projectId
          }
      }
  `,
  });

export const createShape = (floorplan: IFloorplan, shape: ISpaceShape) => {
  return graphqlClient.mutate({
    mutation: gql`
        mutation CreateShape {
            createShape(
                floorplan_id: ${floorplan.id}
                polygons: ${packPolygonsMatrixForPersistence(shape.polygons)}
                space_id: ${shape.spaceId || null}
                is_floor_usf: ${shape.isFloorUsf || false}
            ) {
                floorplan_id
                id
                polygons {
                    x
                    y
                }
            }
        }
    `,
  });
};

export const updateShape = (shape: ISpaceShape) => {
  return graphqlClient.mutate({
    mutation: gql`
        mutation UpdateShape {
            updateShape(
                polygons: ${packPolygonsMatrixForPersistence(shape.polygons)}
                space_id: ${shape.spaceId || null}
                is_floor_usf: ${shape.isFloorUsf || false}
                id: ${shape.id}
            ) {
                floorplan_id
                id
                polygons {
                    x
                    y
                }
            }
        }
    `,
  });
};

export const deleteShape = (id: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteShape {
          deleteShape(
              id: ${id}
          ) {
              id
          }
      }
  `,
  });

export type IShapeResponse = {
  isFloorUsf: boolean;
  id: number;
  polygons: { x: number; y: number }[][][];
  floorplanId: number;
  spaceId: number;
};

export const fetchShapes = (floorplanId: number) =>
  graphqlClient.query<{ shapes: IShapeResponse }>({
    fetchPolicy: 'no-cache',

    query: gql`
      query GetShapes {
          shapes: shapes(
              floorplanId: ${floorplanId}
          ) {
              floorplan_id
              id
              polygons{
                  x
                  y
              }
              space_id
              is_floor_usf
          }
      }
  `,
  });

export const deleteSpace = (spaceId: number) =>
  graphqlClient.mutate<{ deleteSpace: Pick<ISpace, 'id' | 'floorId'> }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation DeleteSpace {
          deleteSpace(
              id: ${spaceId}
          ) {
              id
              floorId
          }
      }
  `,
  });

export const getSpace = (spaceId: number) =>
  graphqlClient.query<{ space: ISpace }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetSpace {
          space(id: ${spaceId}) {
              id
              roomNumber
              roomName
              seatCount
              usf
              isAlternativeWorkpoint
              alternativeWorkpointSeats
              allowBlankSeats
              allowBlankUsf
              floorplanPoint {
                  x
                  y
              }
              photos {
                  id
                  name
                  description
                  insertedAt
              }
              notes {
                  id
                  name
                  text
                  insertedAt
              }
              projectSpaceType {
                  id
                  spaceType {
                      id
                      spaceType
                      name
                      isCustom
                  }
                  category {
                      id
                      name
                      parentId
                      defaultSpaceTypeCategory {
                          id
                          category
                          name
                      }
                  }
                  clientFacingName
              }
              spaceType {
                  id
                  spaceType
                  name
                  spaceTypeCategory {
                      id
                      category
                      name
                  }
              }
          }
      }
  `,
  });

export const createProject = (project: IProject) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateProject(
        $clientId: Int
        $city: String
        $createdByUserId: Int
        $projectName: String
        $projectNumber: String
        $requiresRequestToJoin: Boolean
        $state: String
        $countryCode: String
        $status: String
        $type: String
      ) {
        create_project(
          clientId: $clientId
          status: $status
          projectName: $projectName
          city: $city
          state: $state
          countryCode: $countryCode
          requiresRequestToJoin: $requiresRequestToJoin
          type: $type
          createdByUserId: $createdByUserId
          projectNumber: $projectNumber
        ) {
          id
          client {
            id
            name
          }
          projectName
          city
          state
          projectNumber
          type
          year
          startYear
          clientMoveInYear
          isHeadquarters
          createdByUser {
            id
            name
            email
          }
          spaceTypeCategories {
            id
            name
            icon
            defaultSpaceTypeCategory {
              id
              category
              name
            }
          }
          insertedAt
        }
      }
    `,
    variables: {
      clientId: project.client ? project.client.id : null,
      status: project.status || '',
      projectName: project.projectName,
      city: project.city || '',
      state: project.state,
      countryCode: project.countryCode,
      requiresRequestToJoin: project.requiresRequestToJoin,
      type: project.type,
      projectNumber: project.projectNumber,
      createdByUserId: isNumber(project.createdByUserId)
        ? project.createdByUserId
        : null,
      allowBlankLocation: project.allowBlankLocation,
    },
  });

export const updateProject = (project: IProject) =>
  graphqlClient.mutate<{ updatedProject: IProject }>({
    mutation: gql`
      ${PROJECT_FIELDS}
      mutation UpdateProject(
        $city: String
        $state: String
        $countryCode: String
        $clientId: Int
        $createdByUserId: Int
        $projectId: Int
        $projectName: String
        $projectNumber: String
        $requiresRequestToJoin: Boolean
        $status: String
        $year: Int
        $startYear: Int
        $clientMoveInYear: Int
        $isHeadquarters: Boolean
        $headcountType: HeadcountType
        $allowBlankLocation: Boolean
      ) {
        updatedProject: updateProject(
          projectId: $projectId
          clientId: $clientId
          status: $status
          projectName: $projectName
          city: $city
          state: $state
          countryCode: $countryCode
          requiresRequestToJoin: $requiresRequestToJoin
          createdByUserId: $createdByUserId
          projectNumber: $projectNumber
          year: $year
          startYear: $startYear
          clientMoveInYear: $clientMoveInYear
          isHeadquarters: $isHeadquarters
          headcountType: $headcountType
          allowBlankLocation: $allowBlankLocation
        ) {
          ...ProjectFields
        }
      }
    `,
    variables: {
      projectId: project.id,
      clientId: project.client ? project.client.id : null,
      status: project.status || '',
      projectName: project.projectName,
      city: project.city || '',
      state: project.state,
      countryCode: project.countryCode,
      requiresRequestToJoin: project.requiresRequestToJoin,
      type: project.type,
      projectNumber: project.projectNumber,
      createdByUserId: isNumber(project.createdByUserId)
        ? project.createdByUserId
        : null,
      year: project.year || '',
      startYear: project.startYear,
      clientMoveInYear: project.clientMoveInYear,
      isHeadquarters: project.isHeadquarters,
      headcountType: project.headcountType,
      allowBlankLocation: project.allowBlankLocation,
    },
  });

export const archiveProject = (project: IProject) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation ArchiveProject {
          archive_project(projectId: ${project.id}) {
              id
          }
      }
  `,
  });

export const activateProject = (projectId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation ActivateProject {
          project: activate_project(projectId: ${projectId}) {
              id
          }
      }
  `,
  });

export const cloneProject = (params: {
  createdByUserId: number;
  projectId: number;
  projectName: string;
  clientId: number;
  city: string;
  state: string;
  countryCode: string;
  projectNumber: string;
  status?: ProjectStatus;
  requiresRequestToJoin?: boolean;
  selectedBuildingIds?: number[];
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CloneProject(
        $projectId: Int
        $createdByUserId: Int
        $projectName: String
        $clientId: Int
        $city: String
        $state: String
        $countryCode: String
        $projectNumber: String
        $status: String
        $requiresRequestToJoin: Boolean
        $selectedBuildingIds: [Int]
      ) {
        clone_project(
          createdByUserId: $createdByUserId
          projectId: $projectId
          projectName: $projectName
          client_id: $clientId
          city: $city
          state: $state
          countryCode: $countryCode
          projectNumber: $projectNumber
          status: $status
          selectedBuildingIds: $selectedBuildingIds
          requiresRequestToJoin: $requiresRequestToJoin
        ) {
          id
        }
      }
    `,
    variables: params,
  });

export const createBuilding = (building: IBuilding) =>
  graphqlClient.mutate<{ buildings: IBuilding[] }>({
    mutation: gql`
      mutation CreateBuilding(
        $projectId: Int
        $city: String
        $streetAddress: String
        $numberOfFloors: Int
        $state: String
        $countryCode: String
      ) {
        buildings: createBuilding(
          projectId: $projectId
          numberOfFloors: $numberOfFloors
          streetAddress: $streetAddress
          city: $city
          state: $state
          countryCode: $countryCode
        ) {
          id
          streetAddress
          city
          state
          countryCode
          squareFeet
          programmingUsf
          floors {
            id
            name
            floorNumber
            totalFloorUsf
            isMeasuredFromFloorplan
          }
        }
      }
    `,
    variables: building,
  });

export const saveEditingBuilding = (
  building: IBuilding & { id?: number; projectId: number }
) =>
  graphqlClient.mutate<{ buildings: IBuilding[] }>({
    mutation: gql`
      mutation updateBuilding(
        $id: Int
        $projectId: Int
        $streetAddress: String
        $state: String
        $city: String
        $countryCode: String
        $esaRsfLoadFactor: Float
        $programmingUsf: Float
      ) {
        buildings: update_building(
          id: $id
          projectId: $projectId
          streetAddress: $streetAddress
          city: $city
          state: $state
          countryCode: $countryCode
          programmingUsf: $programmingUsf
          esaRsfLoadFactor: $esaRsfLoadFactor
        ) {
          id
          streetAddress
          city
          state
          countryCode
          squareFeet
          circulationPercentage
          circulation
          programSubtotalUsf
          programmingUsf
          esaRsfLoadFactor
          floors {
            id
            name
            floorNumber
            totalFloorUsf
            isMeasuredFromFloorplan
            rsfLoadFactor
            totalFloorRsf
            isRsfTotalLocked
          }
        }
      }
    `,
    variables: {
      ...building,
    },
  });

export const deleteBuilding = (buildingId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteBuilding($buildingId: Int) {
        deleteBuilding(buildingId: $buildingId) {
          id
          city
          streetAddress
          state
          squareFeet
          floors {
            id
            name
            floorNumber
          }
        }
      }
    `,
    variables: {
      buildingId,
    },
  });

export const bulkUpdateBuildingIndices = (
  buildings: Pick<IBuilding, 'id' | 'index'>[]
) =>
  graphqlClient.mutate<{ buildings: Pick<IBuilding, 'id' | 'index'>[] }>({
    mutation: gql`
      mutation BulkUpdateBuildingIndices($buildings: [BuildingUpdate!]!) {
        buildings: bulkUpdateBuildings(buildings: $buildings) {
          id
          index
        }
      }
    `,
    variables: {
      buildings: buildings.map((building) => ({
        id: building.id,
        building: { index: building.index },
      })),
    },
  });

export const fetchPhotosByRound = (roundId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchPhotosByRound {
          roundPhotos: roundPhotos(round_id: ${roundId}) {
              id,
              description,
              round_id,
              space_id
          }
      }
  `,
  });

export const updateBuildingFloor = (floor: IFloor, updatesUsf: boolean) =>
  graphqlClient.mutate<{ updatedBuilding: IBuilding }>({
    mutation: gql`
      mutation UpdateBuildingFloor(
        $id: Int!
        $name: String
        $floorNumber: Int
        $totalFloorUsf: Float
        $rsfLoadFactor: Float
        $totalFloorRsf: Float
        $isRsfTotalLocked: Boolean
        $isMeasuredFromFloorplan: Boolean
      ) {
        updatedBuilding: updateFloor(
          id: $id
          name: $name
          floorNumber: $floorNumber
          isMeasuredFromFloorplan: $isMeasuredFromFloorplan
          totalFloorUsf: $totalFloorUsf
          rsfLoadFactor: $rsfLoadFactor
          totalFloorRsf: $totalFloorRsf
          isRsfTotalLocked: $isRsfTotalLocked
        ) {
          id
          city
          state
          streetAddress
          squareFeet
          programmingUsf
          esaRsfLoadFactor
          circulationPercentage
          circulation
          programSubtotalUsf
          floors {
            id
            name
            floorNumber
            totalFloorUsf
            isMeasuredFromFloorplan
            rsfLoadFactor
            totalFloorRsf
            isRsfTotalLocked
            floorplan {
              id
            }
          }
        }
      }
    `,
    variables: {
      floorNumber: floor.floorNumber,
      id: floor.id,
      name: floor.name,
      isMeasuredFromFloorplan: floor.isMeasuredFromFloorplan,
      totalFloorUsf: updatesUsf ? floor.totalFloorUsf : undefined,
      rsfLoadFactor: floor.rsfLoadFactor,
      totalFloorRsf: floor.totalFloorRsf,
      isRsfTotalLocked: floor.isRsfTotalLocked,
    },
  });

export const deleteBuildingFloor = (floor: IFloor) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteBuildingFloor {
          building: deleteFloor(
              id: ${floor.id}
          ) {
              id
              index
              city
              state
              countryCode
              streetAddress
              squareFeet
              circulationPercentage
              circulation
              programSubtotalUsf
              programmingUsf
              esaRsfLoadFactor
              headcount {
                  client
                  architect
              }
              floors {
                  id
                  name
                  floorNumber
                  totalFloorUsf
                  isMeasuredFromFloorplan
                  rsfLoadFactor
                  totalFloorRsf
                  isRsfTotalLocked
                  floorplan {
                      id
                  }
                  headcount {
                      client
                      architect
                  }
              }
          }
      }`,
  });

export const insertFloorAbove = (floor: IFloor) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation InsertFloorAbove($id: Int, $newFloorName: String) {
        insertFloorAbove(id: $id, newFloorName: $newFloorName) {
          id
          city
          state
          streetAddress
          squareFeet
          programmingUsf
          esaRsfLoadFactor
          circulationPercentage
          circulation
          programSubtotalUsf
          headcount {
            client
            architect
          }
          floors {
            id
            name
            floorNumber
            totalFloorUsf
            isMeasuredFromFloorplan
            rsfLoadFactor
            totalFloorRsf
            isRsfTotalLocked
            headcount {
              client
              architect
            }
            floorplan {
              id
            }
          }
        }
      }
    `,
    variables: {
      id: floor.id,
      newFloorName: floor.name,
    },
  });

export const insertFloorBelow = (floor: IFloor) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation InsertFloorBelow($id: Int, $newFloorName: String) {
        insertFloorBelow(id: $id, newFloorName: $newFloorName) {
          id
          city
          state
          streetAddress
          squareFeet
          programmingUsf
          esaRsfLoadFactor
          circulationPercentage
          circulation
          programSubtotalUsf
          headcount {
            client
            architect
          }
          floors {
            id
            name
            floorNumber
            totalFloorUsf
            isMeasuredFromFloorplan
            rsfLoadFactor
            totalFloorRsf
            isRsfTotalLocked
            headcount {
              client
              architect
            }
            floorplan {
              id
            }
          }
        }
      }
    `,
    variables: {
      id: floor.id,
      newFloorName: floor.name,
    },
  });

export const moveFloorUp = (floor: IFloor) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation MoveFloorUp {
          moveFloorUp(
              id: ${floor.id}
          ) {
              id
              city
              state
              streetAddress
              squareFeet
              programmingUsf
              esaRsfLoadFactor
              circulationPercentage
              circulation
              programSubtotalUsf
              headcount {
                  client
                  architect
              }
              floors {
                  id
                  name
                  floorNumber
                  totalFloorUsf
                  isMeasuredFromFloorplan
                  rsfLoadFactor
                  totalFloorRsf
                  isRsfTotalLocked
                  headcount {
                      client
                      architect
                  }
                  floorplan {
                      id
                  }
              }
          }
      }`,
  });

export const moveFloorDown = (floor: IFloor) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation MoveFloorDown {
          moveFloorDown(
              id: ${floor.id}
          ) {
              id
              city
              state
              streetAddress
              squareFeet
              programmingUsf
              esaRsfLoadFactor
              circulationPercentage
              circulation
              programSubtotalUsf
              headcount {
                  client
                  architect
              }
              floors {
                  id
                  name
                  floorNumber
                  totalFloorUsf
                  isMeasuredFromFloorplan
                  rsfLoadFactor
                  totalFloorRsf
                  isRsfTotalLocked
                  headcount {
                      client
                      architect
                  }
                  floorplan {
                      id
                  }
              }
          }
      }`,
  });

export const uploadFloorplanPDF = (floorId: number, pdf: File) =>
  graphqlClient.mutate<{ createFloorplanPdf: string }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation UploadFloorplanPDF($floorId: ID!, $pdf: Upload!) {
        createFloorplanPdf(floorId: $floorId, pdf: $pdf)
      }
    `,
    variables: {
      floorId,
      pdf,
    },
  });

export const updateFloorplan = (floorId: number, image: File) =>
  graphqlClient.mutate<{
    floorplan: Pick<IFloorplan, 'id' | 'floorId' | 's3Path'>;
  }>({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation UpdateFloorplan($floorId: ID!, $image: Upload!) {
        floorplan: updateFloorplan(floorId: $floorId, image: $image) {
          id
          floorId
          s3Path
        }
      }
    `,
    variables: {
      floorId,
      image,
    },
  });

export const fetchFloorplanCalibrationApi = (floorplanId: number) =>
  graphqlClient.query<{ calibration: ICalibration }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetFloorplanCalibration($floorplanId: Int!) {
        calibration: floorplanCalibration(floorplanId: $floorplanId) {
          floorplanId
          id
          x1
          x2
          y1
          y2
          constant
        }
      }
    `,
    variables: {
      floorplanId,
    },
  });

export const deleteFloorCalibration = (floorplanId: number) =>
  graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation DeleteFloorCalibration {
          deleteCalibration(
              floorplanId: ${floorplanId}
          ){id, floorplan_id}
      }`,
  });

export const attachNote = (spaceId: number, text: string) =>
  graphqlClient.mutate<{
    note: {
      id: number;
      name: string;
      text: string;
      insertedAt: string;
    };
  }>({
    mutation: gql`
      mutation AttachNote($spaceId: Int!, $text: String!) {
        note: attachNote(spaceId: $spaceId, text: $text) {
          id
          name
          text
          insertedAt
        }
      }
    `,
    variables: {
      spaceId,
      text,
    },
  });

export const getProjectRounds = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectRounds {
          projectRounds(
              projectId: ${projectId},
          ){
              round {
                  id
                  date
                  startTime
                  status
                  individual
                  meeting
                  amenity
                  support
                  buildings {
                      id
                      streetAddress
                      index
                      floors {
                          id
                          name
                          floorNumber
                          totalSpaces
                          completedSpaces
                      }
                  }
              }
              numberOfBuildingsRelatedToRound
              numberOfFloorsRelatedToRound
              totalSpaces
              completedSpaces
          }
      }`,
  });

export const getRoundsBySpace = (
  projectId: number,
  spaceId: number | null = null
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetRoundsBySpace {
          roundsForSpace(spaceId: ${spaceId}) {
              round { id, date, startTime, status }
              numberOfBuildingsRelatedToRound
              numberOfFloorsRelatedToRound
              totalSpaces
              completedSpaces
              space {
                  id
                  seatCount
                  roomNumber
                  photos {
                      id
                      name
                      description
                      insertedAt
                  }
                  notes {
                      roundId
                      text
                  }
                  spaceUsage {
                      id
                      completed
                      usage
                      status
                      seatsInUse
                  }
              }
          }
      }`,
  });

export const getOccupancyAndUtilizationSummaries = (
  roundIds: number[],
  buildingId: number | null,
  floorId: number | null
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOccupancyAndUtilizationSummaries {
          getOccupancyAndUtilizationSummaries(
              roundIds: [${roundIds}]
              buildingId: ${buildingId}
              floorId: ${floorId}
          ) {
              all {
                  roundsWithLowestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  roundsWithHighestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  seats {
                      existing
                      observed
                      inUse
                      averageUtilization
                      averageSeatsUsedPerSpace
                  }
                  spaces {
                      existing
                      observed
                      inUse
                      averageOccupancy
                  }
              }
              individualWorkspaces {
                  roundsWithLowestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  roundsWithHighestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  seats {
                      existing
                      observed
                      inUse
                      averageUtilization
                      averageSeatsUsedPerSpace
                  }
                  spaces {
                      existing
                      observed
                      inUse
                      averageOccupancy
                  }
              }
              meetingSpaces {
                  roundsWithLowestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  roundsWithHighestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  seats {
                      existing
                      observed
                      inUse
                      averageUtilization
                      averageSeatsUsedPerSpace
                  }
                  spaces {
                      existing
                      observed
                      inUse
                      averageOccupancy
                  }
              }
              amenitySpaces {
                  roundsWithLowestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  roundsWithHighestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  seats {
                      existing
                      observed
                      inUse
                      averageUtilization
                      averageSeatsUsedPerSpace
                  }
                  spaces {
                      existing
                      observed
                      inUse
                      averageOccupancy
                  }
              }
              supportSpaces {
                  roundsWithLowestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  roundsWithHighestOccupancy {
                      id
                      occupancy
                      observedSpacesInUse
                      observedSpaces
                      date
                      startTime
                  }
                  seats {
                      existing
                      observed
                      inUse
                      averageUtilization
                      averageSeatsUsedPerSpace
                  }
                  spaces {
                      existing
                      observed
                      inUse
                      averageOccupancy
                  }
              }
          }
      }
  `,
  });

export const getOccupancyAndUtilizationExcel = (
  projectId: number,
  shouldShowClientFacingNames?: boolean
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetOccupancyAndUtilizationExcel(
        $projectId: Int!
        $shouldShowClientFacingNames: Boolean
      ) {
        getOccupancyAndUtilizationExcel(
          projectId: $projectId
          showClientFacingNames: $shouldShowClientFacingNames
        )
      }
    `,
    variables: { projectId, shouldShowClientFacingNames },
  });

export const userFeedback = (
  feedback: IFeedback,
  device: IDeviceInfo,
  screenshots: string[]
) =>
  graphqlClient.mutate<{ userFeedback: { status: string } }>({
    mutation: gql`
      mutation UserFeedback(
        $feedback: UserFeedback!
        $device: DeviceInfo!
        $screenshots: [String]!
      ) {
        userFeedback(
          feedback: $feedback
          device: $device
          screenshots: $screenshots
        ) {
          status
        }
      }
    `,
    variables: {
      feedback,
      device,
      screenshots,
    },
  });

export const downloadPhotos = (ids: number[]) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query DownloadPhotos($ids: [Int]!) {
        downloadPhotos(ids: $ids)
      }
    `,
    variables: {
      ids,
    },
  });

export const createCustomSpaceType = (
  projectId: number,
  parentId: number,
  spaceType: Partial<ISpaceType>,
  previousId?: number,
  clientFacingName?: string
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateCustomSpaceType(
        $projectId: ID!
        $spaceType: SpaceTypeInput!
        $parentId: ID!
        $previousId: Int
        $clientFacingName: String
      ) {
        createCustomSpaceType(
          projectId: $projectId
          parentId: $parentId
          previousId: $previousId
          spaceType: $spaceType
          clientFacingName: $clientFacingName
        ) {
          id
          previousId
          spaceType {
            id
          }
          category {
            id
            name
            includeInTotalWorkSeats
            defaultSpaceTypeCategory {
              id
              category
              name
            }
          }
        }
      }
    `,
    variables: {
      spaceType,
      projectId,
      parentId,
      previousId,
      clientFacingName,
    },
  });

export const updateCustomSpaceType = (
  spaceTypeId: number,
  spaceType: Partial<ISpaceType>,
  clientFacingName?: string
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation UpdateCustomSpaceType($spaceType: SpaceTypeInput! $clientFacingName: String) {
          customSpaceType: updateCustomSpaceType(
              spaceTypeId: ${spaceTypeId}
              spaceType: $spaceType
              clientFacingName: $clientFacingName
          ) {
              id
              spaceType
              name
              isCustom
              icon
          }
      }
  `,
    variables: { spaceType, clientFacingName },
  });

export const deleteCustomSpaceType = (projectId: number, spaceTypeId: number) =>
  graphqlClient.mutate<{ deletedSpaceType: { id: number } }>({
    mutation: gql`
      mutation DeleteCustomSpaceType($projectId: ID!, $spaceTypeId: ID!) {
        deletedSpaceType: deleteCustomSpaceType(
          projectId: $projectId
          spaceTypeId: $spaceTypeId
        ) {
          id
        }
      }
    `,
    variables: {
      projectId,
      spaceTypeId,
    },
  });

export const deleteNote = (noteId: number) => {
  return graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
        mutation DeleteNote {
            deleteNote(id: ${noteId}) {
                id
                spaceId
            }
        }`,
  });
};

export const updateNote = (noteId: number, newNote: Partial<INote>) => {
  return graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
        mutation UpdateNote ($newNote: NoteAttrs!) {
            updateNote(id: ${noteId}, note: $newNote) {
                id
                text
                name
                insertedAt
                spaceId
            }
        }
    `,
    variables: { newNote },
  });
};

export const putWorksheet = (worksheet: Partial<IWorksheet>) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation PutWorksheet($worksheet: WorksheetArgs!) {
        putWorksheet(worksheet: $worksheet) {
          id
          scenarioId
          spaceAllocation
          worksheetRows {
            quantity
            seatCap
            sizeX
            sizeY
            usf
            ratio
            spaceId
            projectSpaceType {
              id
              previousId
              spaceType {
                id
                spaceType
                name
                isCustom
                icon
              }
              category {
                id
                name
                parentId
                defaultSpaceTypeCategory {
                  id
                  category
                  name
                  parentId
                }
              }
            }
            id
            child
          }
          categories {
            categoryId
            visible
          }
        }
      }
    `,
    variables: { worksheet },
  });

export const postWorksheetRow = (
  worksheetRow: Partial<IWorksheetRow>,
  worksheetId: number
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation PostWorksheetRow(
        $worksheetRow: WorksheetRowArgs!
        $worksheetId: Int!
      ) {
        worksheetRow: postWorksheetRow(
          worksheetRow: $worksheetRow
          worksheetId: $worksheetId
        ) {
          id
          quantity
          seatCap
          sizeX
          sizeY
          usf
          ratio
          spaceId
          projectSpaceType {
            id
            previousId
            spaceType {
              id
              spaceType
              name
              isCustom
              icon
            }
            category {
              id
              name
              parentId
              defaultSpaceTypeCategory {
                id
                category
                name
              }
            }
          }
          id
          child
        }
      }
    `,
    variables: { worksheetRow, worksheetId },
  });

export const createWorksheetNote = (
  worksheetNote: ICategoryNote,
  worksheetId: number
) => {
  return graphqlClient.mutate({
    mutation: gql`
      mutation CreateWorksheetNote(
        $worksheetNote: CategoryNoteInput!
        $worksheetId: Int!
      ) {
        createWorksheetNote(
          worksheetNote: $worksheetNote
          worksheetId: $worksheetId
        ) {
          id
          note
        }
      }
    `,
    variables: {
      worksheetNote,
      worksheetId,
    },
  });
};

export const createCluster = (cluster: ICluster) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateCluster($cluster: ClusterArgs!) {
        cluster: createCluster(cluster: $cluster) {
          id
        }
      }
    `,
    variables: { cluster },
  });

export const getClusters = (floorId: number) =>
  graphqlClient.query<{ clusters: ICluster[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetClusters {
          clusters(floorId: ${floorId}) {
              id
              number
              roomName
              seatCount
              usf
              notes
              floorplanPoint {
                  x
                  y
              }
              projectSpaceType {
                  id
                  spaceType {
                      id
                      name
                      isCustom
                  }
                  category {
                      id
                      name
                      icon
                      defaultSpaceTypeCategory {
                          id
                          name
                          category
                      }
                      parentId
                      includeInTotalWorkSeats
                  }
                  clientFacingName
              }
              spaceType {
                  id
                  name
                  spaceType
                  spaceTypeCategory {
                      id
                      category
                  }
                  icon
              }
          }
      }`,
  });

export const fetchProjectColors = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchProjectColors {
          projectColors(projectId: ${projectId}) {
              id
              individualWorkspaces
              workstations
              workstationsGradientColorIndex
              privateOffices
              privateOfficesGradientColorIndex
              customSubcategory
              customSubcategoryGradientColorIndex
              meetingSpaces
              amenitySpaces
              supportSpaces
              sharedSpaces
              customWorkspaceColors
              customMeetingSpaceColors
              customAmenitySpaceColors
              customSupportSpaceColors
              projectId
          }
      }`,
  });

export const updateProjectColors = (projectColors: IProjectColors) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation UpdateProjectColors($projectColors: ProjectColorsArgs!) {
        projectColors: updateProjectColors(projectColors: $projectColors) {
          id
          individualWorkspaces
          workstations
          workstationsGradientColorIndex
          privateOffices
          privateOfficesGradientColorIndex
          customSubcategory
          customSubcategoryGradientColorIndex
          meetingSpaces
          amenitySpaces
          supportSpaces
          sharedSpaces
          customWorkspaceColors
          customMeetingSpaceColors
          customAmenitySpaceColors
          customSupportSpaceColors
          projectId
        }
      }
    `,
    variables: {
      projectColors: omit(removeTypeNameFromObject(projectColors), [
        'workstationsGradientColorIndex',
        'privateOfficesGradientColorIndex',
        'customSubcategoryGradientColorIndex',
      ]),
    },
  });

export const postOrganizationColors = (organizationColors: IProjectColors) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation PostOrganizationColors(
        $organizationColors: OrganizationColorsArgs!
      ) {
        createOrganizationColors(organizationColors: $organizationColors) {
          id
          meetingSpaces
          amenitySpaces
          supportSpaces
          sharedSpaces
          otherSpaces
          individualWorkspaces
          customWorkspaceColors
          customMeetingSpaceColors
          customAmenitySpaceColors
          customSupportSpaceColors
          organizationId
        }
      }
    `,
    variables: {
      organizationColors: removeTypeNameFromObject(organizationColors),
    },
  });

export const updateOrganizationColors = (organizationColors: IProjectColors) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation UpdateOrganizationColors(
        $organizationColors: OrganizationColorsArgs!
      ) {
        updateOrganizationColors(organizationColors: $organizationColors) {
          id
          meetingSpaces
          amenitySpaces
          supportSpaces
          sharedSpaces
          otherSpaces
          individualWorkspaces
          customWorkspaceColors
          customMeetingSpaceColors
          customAmenitySpaceColors
          customSupportSpaceColors
          organizationId
        }
      }
    `,
    variables: {
      organizationColors: removeTypeNameFromObject(organizationColors),
    },
  });

export const deleteCluster = (id: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteCluster {
          deleteCluster(id: ${id}) {
              id
          }
      }
  `,
  });

export const createTotalProgram = (totalProgram: {
  name: string;
  index?: number;
  totalProgramScenario: { scenarioId: number }[];
  projectId: number;
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation CreateTotalProgram($totalProgram: TotalProgramArgs!) {
        createTotalProgram(totalProgram: $totalProgram) {
          id
          index
          name
          subtotalProgramMultiplier
          planningFactor
          usf
          totalProgramScenario {
            id
            scenarioId
            totalProgramId
          }
        }
      }
    `,
    variables: { totalProgram: removeTypeNameFromObject(totalProgram) },
  });

export const createTotalProgramNote = (
  totalProgramNote: ICategoryNote,
  totalProgramId: number
) => {
  return graphqlClient.mutate({
    mutation: gql`
      mutation CreateTotalProgramNote(
        $totalProgramNote: CategoryNoteInput!
        $totalProgramId: Int!
      ) {
        createTotalProgramNote(
          totalProgramNote: $totalProgramNote
          totalProgramId: $totalProgramId
        ) {
          id
          note
        }
      }
    `,
    variables: {
      totalProgramNote,
      totalProgramId,
    },
  });
};

export const createChildWorksheetSpaceType = (childWorksheetSpaceType: {
  name: string;
  clientFacingName?: string;
  projectId: number;
  spaceTypeId: number;
  unitsHigh: number;
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation CreateChildWorksheetSpaceType(
        $childWorksheetSpaceType: ChildWorksheetSpaceTypeArgs!
      ) {
        childWorksheetSpaceType: createChildWorksheetSpaceType(
          childWorksheetSpaceType: $childWorksheetSpaceType
        ) {
          id
          projectId
          spaceTypeId
          name
          clientFacingName
          unitsHigh
          rank
        }
      }
    `,
    variables: {
      childWorksheetSpaceType: removeTypeNameFromObject(
        childWorksheetSpaceType
      ),
    },
  });

export const updateChildWorksheetSpaceType = (childWorksheetSpaceType: {
  name: string;
  clientFacingName?: string;
  id: number;
  unitsHigh: number;
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation UpdateChildWorksheetSpaceType(
        $childWorksheetSpaceType: ChildWorksheetSpaceTypeArgs!
      ) {
        updateChildWorksheetSpaceType(
          childWorksheetSpaceType: $childWorksheetSpaceType
        ) {
          id
          name
          clientFacingName
          projectId
          spaceTypeId
          unitsHigh
          rank
        }
      }
    `,
    variables: {
      childWorksheetSpaceType: removeTypeNameFromObject(
        childWorksheetSpaceType
      ),
    },
  });

export const fetchAllChildWorksheetSpaces = (projectId: number) =>
  graphqlClient.query<{ childWorksheetSpaceTypes: IChildWorksheetSpaceType[] }>(
    {
      fetchPolicy: 'no-cache',
      query: gql`
        query FetchAllChildWorksheetSpaces {
            childWorksheetSpaceTypes(projectId: ${projectId}) {
                id
                name
                clientFacingName
                projectId
                spaceTypeId
                unitsHigh
                rank
            }
        }`,
    }
  );

export const fetchUserFavoriteProjects = (type: string) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchUserFavoriteProjects {
          userFavoriteProjectsLegacy: getUserFavoriteProjects(type: "${type}") {
              id
              index
              userId
              projectId
          }
      }
  `,
  });

export const createUserFavoriteProject = (projectId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateUserFavoriteProject {
          createUserFavoriteProject(projectId: ${projectId}) {
              id
          }
      }
  `,
  });

export const deleteUserFavoriteProject = ({
  userFavoriteProjectId,
  projectId,
}: {
  userFavoriteProjectId?: number;
  projectId?: number;
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteUserFavoriteProject {
          deleteUserFavoriteProject(${
            projectId
              ? `projectId: ${projectId}`
              : `id: ${userFavoriteProjectId}`
          }) {
          id
      }
      }
  `,
  });

export const getOrganizationColors = (id: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      {
          organizationColors(${id}) {
          id
      }
      }
  `,
  });

export const fetchProjectSettings = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchProjectSettings {
          projectSettings(id: ${projectId}) {
              settings {
                  key
                  value
                  project_id
                  organization_id
                  user_id
                  created_by_id
                  updated_by_id
              }
          }
      }
  `,
  });

export const postProjectSettings = ({
  settings,
  projectId,
}: {
  settings: Record<string, string>[];
  projectId: number;
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation PostProjectSettings(
        $settings: [KeyValueInput]!
        $projectId: Int!
      ) {
        postProjectSettings(settings: $settings, projectId: $projectId) {
          settings {
            key
            value
            project_id
            organization_id
            user_id
            created_by_id
            updated_by_id
          }
        }
      }
    `,
    variables: {
      settings,
      projectId,
    },
  });

export const updateProjectSpaceType = ({
  categoryId,
  id,
  projectId,
  spaceTypeId,
  previousId,
  icon,
  clientFacingName,
}: {
  categoryId?: number;
  id?: number;
  projectId?: number;
  spaceTypeId?: number;
  previousId?: number | null;
  icon?: string | null;
  clientFacingName?: string;
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation UpdateProjectSpaceType(
        $projectSpaceType: ProjectSpaceTypeInput!
      ) {
        updateProjectSpaceType(projectSpaceType: $projectSpaceType) {
          id
          previousId
          projectId
          icon
          spaceType {
            id
            spaceType
            name
            isCustom
            icon
          }
          category {
            id
            name
            parentId
            defaultSpaceTypeCategory {
              id
              category
              name
            }
          }
        }
      }
    `,
    variables: {
      projectSpaceType: {
        categoryId,
        id,
        projectId,
        icon,
        spaceTypeId,
        previousId,
        clientFacingName,
      },
    },
  });

export const deleteSpaceTypeFromProject = (projectSpaceTypeId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteSpaceTypeFromProject($projectSpaceTypeId: Int!) {
        project_space_type: deleteSpaceTypeFromProject(
          projectSpaceTypeId: $projectSpaceTypeId
        ) {
          id
        }
      }
    `,
    variables: {
      projectSpaceTypeId,
    },
  });

export const deleteChildSpaceTypeFromProject = (childSpaceTypeId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteChildSpaceTypeFromProject($childSpaceTypeId: Int!) {
        child_space_type: deleteChildSpaceTypeFromProject(
          childSpaceTypeId: $childSpaceTypeId
        ) {
          id
        }
      }
    `,
    variables: {
      childSpaceTypeId,
    },
  });

export const deleteSpaceTypeRowsFromProject = (projectSpaceTypeId: number) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteSpaceTypeRowsFromProject($projectSpaceTypeId: Int!) {
        project_space_type: deleteSpaceTypeRowsFromProject(
          projectSpaceTypeId: $projectSpaceTypeId
        ) {
          id
        }
      }
    `,
    variables: {
      projectSpaceTypeId,
    },
  });

export const deleteChildWorksheetSpaceRowsFromProject = (
  childWorksheetSpaceId: number
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation DeleteChildWorksheetSpaceRowsFromProject(
        $childWorksheetSpaceId: Int!
      ) {
        deleteRows: deleteChildWorksheetSpaceRowsFromProject(
          childWorksheetSpaceId: $childWorksheetSpaceId
        ) {
          id
        }
      }
    `,
    variables: {
      childWorksheetSpaceId,
    },
  });

export const testError = () =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query TestError {
        testError(error: "exception")
      }
    `,
  });

export const fetchSpacesWorksheetSpreadsheet = (params: {
  floorId: number;
  clientFacingNamesAreVisible?: boolean;
}) =>
  graphqlClient.query<{ spreadsheet: { name: string; data: string } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query FetchSpacesWorksheetSpreadsheet(
        $floorId: Int!
        $clientFacingNamesAreVisible: Boolean
      ) {
        spreadsheet: spacesWorksheetSpreadsheet(
          floorId: $floorId
          showClientFacingNames: $clientFacingNamesAreVisible
        ) {
          name
          data
        }
      }
    `,
    variables: params,
  });
